import React, { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react';
import { notificationsAPI } from '../../services/api';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
    const [error, setError] = useState('');

    useEffect(() => {
        fetchNotifications();
    }, [filter]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const params = filter === 'unread' ? { unread_only: true } : {};
            const response = await notificationsAPI.getNotifications(params);
            const notificationsData = Array.isArray(response.data) ? response.data : (response.data?.notifications || []);
            setNotifications(notificationsData);
            setError('');
        } catch (err) {
            setError('Failed to load notifications');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await notificationsAPI.markAsRead(notificationId);
            fetchNotifications();
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationsAPI.markAllAsRead();
            fetchNotifications();
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            await notificationsAPI.deleteNotification(notificationId);
            fetchNotifications();
        } catch (err) {
            console.error('Failed to delete notification:', err);
        }
    };

    const getNotificationIcon = (type) => {
        const icons = {
            achievement: '🏆',
            streak: '🔥',
            reminder: '⏰',
            social: '👥',
            system: '📢',
            course: '📚',
            default: '🔔'
        };
        return icons[type] || icons.default;
    };

    const getNotificationColor = (type) => {
        const colors = {
            achievement: '#FFD700',
            streak: '#FF6B35',
            reminder: '#4ECDC4',
            social: '#95E1D3',
            system: '#6C5CE7',
            course: '#27B05B'
        };
        return colors[type] || '#666';
    };

    const filteredNotifications = notifications.filter(n => {
        if (filter === 'unread') return !n.is_read;
        if (filter === 'read') return n.is_read;
        return true;
    });

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Loading notifications...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Bell size={32} color="#27B05B" />
                    <h1 style={styles.title}>Notifications</h1>
                </div>
                {notifications.some(n => !n.is_read) && (
                    <button onClick={markAllAsRead} style={styles.markAllBtn}>
                        <CheckCheck size={18} />
                        Mark All as Read
                    </button>
                )}
            </div>

            <div style={styles.filters}>
                <button
                    onClick={() => setFilter('all')}
                    style={{...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {})}}
                >
                    All ({notifications.length})
                </button>
                <button
                    onClick={() => setFilter('unread')}
                    style={{...styles.filterBtn, ...(filter === 'unread' ? styles.filterBtnActive : {})}}
                >
                    Unread ({notifications.filter(n => !n.is_read).length})
                </button>
                <button
                    onClick={() => setFilter('read')}
                    style={{...styles.filterBtn, ...(filter === 'read' ? styles.filterBtnActive : {})}}
                >
                    Read ({notifications.filter(n => n.is_read).length})
                </button>
            </div>

            {error && (
                <div style={styles.error}>
                    <X size={20} />
                    <span>{error}</span>
                </div>
            )}

            <div style={styles.notificationsList}>
                {filteredNotifications.length === 0 ? (
                    <div style={styles.empty}>
                        <Bell size={64} color="#ccc" />
                        <h3>No notifications</h3>
                        <p>You're all caught up! Check back later for updates.</p>
                    </div>
                ) : (
                    filteredNotifications.map(notification => (
                        <div
                            key={notification.id}
                            style={{
                                ...styles.notificationCard,
                                ...(notification.is_read ? {} : styles.notificationUnread)
                            }}
                        >
                            <div
                                style={{
                                    ...styles.notificationIcon,
                                    backgroundColor: getNotificationColor(notification.type)
                                }}
                            >
                                <span style={styles.iconEmoji}>
                                    {getNotificationIcon(notification.type)}
                                </span>
                            </div>

                            <div style={styles.notificationContent}>
                                <h3 style={styles.notificationTitle}>{notification.title}</h3>
                                <p style={styles.notificationMessage}>{notification.message}</p>
                                <span style={styles.notificationTime}>
                                    {new Date(notification.created_at).toLocaleString()}
                                </span>
                            </div>

                            <div style={styles.notificationActions}>
                                {!notification.is_read && (
                                    <button
                                        onClick={() => markAsRead(notification.id)}
                                        style={styles.actionBtn}
                                        title="Mark as read"
                                    >
                                        <Check size={18} />
                                    </button>
                                )}
                                <button
                                    onClick={() => deleteNotification(notification.id)}
                                    style={{...styles.actionBtn, ...styles.deleteBtn}}
                                    title="Delete"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#2C3E50',
        margin: 0
    },
    markAllBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    filters: {
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        flexWrap: 'wrap'
    },
    filterBtn: {
        padding: '10px 20px',
        backgroundColor: 'white',
        color: '#666',
        border: '2px solid #E0E0E0',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    filterBtnActive: {
        backgroundColor: '#27B05B',
        color: 'white',
        borderColor: '#27B05B'
    },
    notificationsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    },
    notificationCard: {
        display: 'flex',
        gap: '15px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        alignItems: 'flex-start'
    },
    notificationUnread: {
        backgroundColor: '#F0F9F4',
        borderLeft: '4px solid #27B05B'
    },
    notificationIcon: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    iconEmoji: {
        fontSize: '24px'
    },
    notificationContent: {
        flex: 1,
        minWidth: 0
    },
    notificationTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#2C3E50',
        margin: '0 0 8px 0'
    },
    notificationMessage: {
        fontSize: '14px',
        color: '#666',
        margin: '0 0 8px 0',
        lineHeight: '1.5'
    },
    notificationTime: {
        fontSize: '12px',
        color: '#999'
    },
    notificationActions: {
        display: 'flex',
        gap: '8px',
        flexShrink: 0
    },
    actionBtn: {
        padding: '8px',
        backgroundColor: 'transparent',
        border: '1px solid #E0E0E0',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#666',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    deleteBtn: {
        color: '#E74C3C',
        borderColor: '#E74C3C'
    },
    empty: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#999'
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '15px',
        backgroundColor: '#FFE6E6',
        color: '#E74C3C',
        borderRadius: '8px',
        marginBottom: '20px'
    },
    loading: {
        textAlign: 'center',
        padding: '60px 20px'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
    }
};

export default Notifications;
